(function () {
  'use strict';

  var DAY = 86400000;
  var EPOCH = Date.UTC(2020, 0, 1) / DAY;
  var TIMEZONE = 'America/New_York';
  var formatters = new Map();
  var thanksgivingDates = new Map();

  function hash(value) {
    var result = 2166136261;
    for (var i = 0; i < value.length; i += 1) {
      result ^= value.charCodeAt(i);
      result = Math.imul(result, 16777619);
    }
    return result >>> 0;
  }

  function seasonOf(product) {
    var tags = Array.isArray(product.tags) ? product.tags.join(' ') : String(product.tags || '');
    var text = (String(product.title || '') + ' ' + tags).toLowerCase().replace(/[_–—-]+/g, ' ');
    if (/\b(halloween|spooky|haunted|witch(?:es)?|jack o lantern|trick or treat)\b/.test(text)) return 'halloween';
    if (/\b(christmas|xmas|santa|santa claus|reindeer|gingerbread|candy canes?|yuletide|noel|t[’']?is the season)\b/.test(text)) return 'christmas';
    if (/\b(thanksgiving|autumn|fall|harvest|pumpkins?)\b/.test(text)) return 'thanksgiving';
    return 'everyday';
  }

  function dayOrdinal(key) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key));
    if (!match) throw new Error('Invalid calendar date');
    var time = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    var actual = new Date(time).toISOString().slice(0, 10);
    if (actual !== key) throw new Error('Invalid calendar date');
    return Math.floor(time / DAY);
  }

  function dateKey(value, timezone) {
    var zone = timezone || TIMEZONE;
    if (!formatters.has(zone)) formatters.set(zone, new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }));
    var parts = formatters.get(zone).formatToParts(value instanceof Date ? value : new Date(value));
    var values = {};
    parts.forEach(function (part) { values[part.type] = part.value; });
    return values.year + '-' + values.month + '-' + values.day;
  }

  function nextMidnightDelay(value, timezone) {
    var now = value instanceof Date ? value.getTime() : Number(value);
    var today = dateKey(now, timezone);
    var low = now;
    var high = now + 36 * 3600000;
    while (high - low > 250) {
      var middle = Math.floor((low + high) / 2);
      if (dateKey(middle, timezone) === today) low = middle;
      else high = middle;
    }
    return Math.max(250, high - now + 50);
  }

  function inWindow(season, monthDay, year) {
    if (typeof monthDay === 'string') {
      dayOrdinal(monthDay);
      year = Number(monthDay.slice(0, 4));
      monthDay = Number(monthDay.slice(5, 7)) * 100 + Number(monthDay.slice(8, 10));
    }
    if (season === 'everyday') return true;
    if (season === 'halloween') return monthDay >= 1027 && monthDay <= 1031;
    if (season === 'thanksgiving') {
      if (!Number.isInteger(year)) throw new Error('Thanksgiving eligibility requires a calendar year');
      if (!thanksgivingDates.has(year)) thanksgivingDates.set(year, 22 + (4 - new Date(Date.UTC(year, 10, 1)).getUTCDay() + 7) % 7);
      var thanksgiving = thanksgivingDates.get(year);
      return monthDay >= 1123 && monthDay <= 1100 + thanksgiving;
    }
    if (season === 'christmas') return monthDay >= 1221 && monthDay <= 1225;
    return false;
  }

  function normalizeProducts(products) {
    var seen = new Set();
    return (Array.isArray(products) ? products : []).map(function (product) {
      return { product: product, key: String(product.id || product.handle || ''), season: seasonOf(product), category: String(product.category || '').toLowerCase() };
    }).filter(function (entry) {
      if (!entry.key || seen.has(entry.key)) return false;
      seen.add(entry.key);
      return true;
    }).sort(function (left, right) { return left.key < right.key ? -1 : left.key > right.key ? 1 : 0; });
  }

  function eligibleProducts(products, key) {
    dayOrdinal(key);
    var monthDay = Number(key.slice(5, 7)) * 100 + Number(key.slice(8, 10));
    var year = Number(key.slice(0, 4));
    return normalizeProducts(products).filter(function (entry) { return inWindow(entry.season, monthDay, year); }).map(function (entry) { return entry.product; });
  }

  function createSelector(products) {
    var records = normalizeProducts(products);
    var seen = new Map();
    var previous = null;
    var nextDay = EPOCH;
    var selections = new Map();
    return function select(key) {
      var target = dayOrdinal(key);
      if (target < EPOCH) throw new Error('Calendar dates before 2020 are not supported');
      if (selections.has(target)) return selections.get(target);
      for (; nextDay <= target; nextDay += 1) {
        var calendar = new Date(nextDay * DAY);
        var monthDay = (calendar.getUTCMonth() + 1) * 100 + calendar.getUTCDate();
        var year = calendar.getUTCFullYear();
        var candidates = records.filter(function (entry) { return inWindow(entry.season, monthDay, year); });
        if (candidates.length > 1 && previous) candidates = candidates.filter(function (entry) { return entry.key !== previous.key; });
        var seasonalSlot = nextDay % 3 === 2;
        var bucket = candidates.filter(function (entry) { return (entry.season !== 'everyday') === seasonalSlot; });
        if (bucket.length) candidates = bucket;
        if (previous) {
          var differentCategory = candidates.filter(function (entry) { return entry.category !== previous.category; });
          if (differentCategory.length) candidates = differentCategory;
        }
        var best = null;
        var oldest = Infinity;
        var bestHash = -1;
        candidates.forEach(function (entry) {
          var last = seen.has(entry.key) ? seen.get(entry.key) : -Infinity;
          var tie = last <= oldest ? hash(nextDay + '|' + entry.key) : -1;
          if (!best || last < oldest || (last === oldest && tie > bestHash)) {
            best = entry;
            oldest = last;
            bestHash = tie;
          }
        });
        if (best) seen.set(best.key, nextDay);
        previous = best;
        selections.set(nextDay, best ? best.product : null);
      }
      return selections.get(target) || null;
    };
  }

  function selectProduct(products, key) {
    return createSelector(products)(key);
  }

  var api = { seasonOf: seasonOf, dateKey: dateKey, dayOrdinal: dayOrdinal, nextMidnightDelay: nextMidnightDelay, inWindow: inWindow, eligibleProducts: eligibleProducts, createSelector: createSelector, selectProduct: selectProduct };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.GGDailyHero) { window.GGDailyHero.scan(); return; }

  var controllers = new Map();

  function capture(node, attributesOnly) {
    if (!node) return null;
    return { node: node, attributesOnly: attributesOnly, attributes: Array.from(node.attributes).map(function (attribute) { return [attribute.name, attribute.value]; }), children: Array.from(node.childNodes).map(function (child) { return child.cloneNode(true); }) };
  }

  function restore(snapshot) {
    if (!snapshot) return;
    Array.from(snapshot.node.attributes).forEach(function (attribute) { snapshot.node.removeAttribute(attribute.name); });
    snapshot.attributes.forEach(function (attribute) { snapshot.node.setAttribute(attribute[0], attribute[1]); });
    if (!snapshot.attributesOnly && snapshot.node.tagName !== 'IMG') snapshot.node.replaceChildren.apply(snapshot.node, snapshot.children.map(function (child) { return child.cloneNode(true); }));
  }

  function variantNumber(value) {
    return String(value || '').split('/').pop();
  }

  function productURL(product) {
    var url = new URL(product.url, window.location.href);
    if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol)) throw new Error('Invalid product link');
    var variant = variantNumber(product.variantId);
    if (!/^\d+$/.test(variant)) throw new Error('Missing pictured variant');
    url.searchParams.set('variant', variant);
    return url.pathname + url.search + url.hash;
  }

  function preload(product, sizes) {
    return new Promise(function (resolve, reject) {
      var image = new Image();
      var timer = window.setTimeout(function () { reject(new Error('Product image timed out')); }, 10000);
      image.onload = function () { window.clearTimeout(timer); resolve(); };
      image.onerror = function () { window.clearTimeout(timer); reject(new Error('Product image unavailable')); };
      image.loading = 'eager';
      image.fetchPriority = 'high';
      if (sizes) image.sizes = sizes;
      if (product.image.srcset) image.srcset = product.image.srcset;
      image.src = product.image.src;
    });
  }

  async function variantAvailable(product) {
    var url = new URL(productURL(product), window.location.origin);
    url.pathname = url.pathname.replace(/\/$/, '') + '.js';
    url.search = '';
    url.hash = '';
    var abort = new AbortController();
    var timer = window.setTimeout(function () { abort.abort(); }, 6000);
    try {
      var response = await window.fetch(url.pathname, { credentials: 'same-origin', cache: 'no-store', signal: abort.signal, headers: { Accept: 'application/json' } });
      if (response.status === 404) return false;
      if (!response.ok) throw new Error('Product availability could not be verified');
      var data = await response.json();
      var variant = (data.variants || []).find(function (entry) { return String(entry.id) === variantNumber(product.variantId); });
      return Boolean(variant && variant.available === true);
    } finally {
      window.clearTimeout(timer);
    }
  }

  function makeController(section) {
    var names = ['eyebrow', 'title', 'description', 'button', 'image-link', 'image', 'note'];
    var nodes = {};
    names.forEach(function (name) { nodes[name] = section.querySelector('[data-gg-daily-' + name + ']'); });
    var snapshots = names.map(function (name) { return capture(nodes[name], name === 'image-link'); });
    var rootAttributes = ['data-gg-hero-product', 'data-gg-hero-variant', 'data-gg-hero-date', 'data-gg-daily-active'].map(function (name) { return [name, section.getAttribute(name)]; });
    var dataNode = section.querySelector('script[data-gg-daily-data]');
    var raw = '';
    var config = null;
    var products = [];
    var select = null;
    var current = '';
    var currentProduct = null;
    var lastChecked = Date.now();
    var token = 0;
    var timer = null;
    var disposed = false;
    var running = false;

    function fallback() {
      snapshots.forEach(restore);
      rootAttributes.forEach(function (entry) { if (entry[1] === null) section.removeAttribute(entry[0]); else section.setAttribute(entry[0], entry[1]); });
      section.removeAttribute('data-gg-daily-pending');
      current = '';
      currentProduct = null;
    }

    function schedule() {
      window.clearTimeout(timer);
      if (!disposed && config && config.enabled) timer = window.setTimeout(function () { refresh(true); }, nextMidnightDelay(Date.now(), config.timezone || TIMEZONE));
    }

    function apply(product, key) {
      var strings = config.strings || {};
      var url = productURL(product);
      if (nodes.eyebrow) nodes.eyebrow.textContent = strings.eyebrow || 'KATE AURORA';
      if (nodes.title && strings.headline) nodes.title.textContent = strings.headline;
      if (nodes.description) nodes.description.textContent = product.title;
      if (nodes.button) {
        var label = nodes.button.querySelector('[data-gg-daily-button-label]');
        if (label) label.textContent = strings.buttonLabel || label.textContent;
        else if (strings.buttonLabel) nodes.button.textContent = strings.buttonLabel;
        nodes.button.setAttribute('href', url);
      }
      if (nodes.note) {
        nodes.note.textContent = product.note || '';
        nodes.note.hidden = !product.note;
      }
      nodes['image-link'].setAttribute('href', url);
      nodes['image-link'].setAttribute('aria-label', String(strings.imageLinkLabel || product.title).split('__PRODUCT__').join(product.title));
      nodes['image-link'].removeAttribute('data-gg-daily-static-link');
      nodes['image-link'].removeAttribute('tabindex');
      nodes['image-link'].removeAttribute('aria-hidden');
      nodes.image.setAttribute('width', String(product.image.width));
      nodes.image.setAttribute('height', String(product.image.height));
      nodes.image.setAttribute('loading', 'eager');
      nodes.image.setAttribute('fetchpriority', 'high');
      nodes.image.setAttribute('alt', product.image.alt || product.title);
      if (product.image.srcset) nodes.image.setAttribute('srcset', product.image.srcset);
      else nodes.image.removeAttribute('srcset');
      nodes.image.setAttribute('src', product.image.src);
      nodes.image.hidden = false;
      section.setAttribute('data-gg-hero-product', String(product.id));
      section.setAttribute('data-gg-hero-variant', variantNumber(product.variantId));
      section.setAttribute('data-gg-hero-date', key);
      section.setAttribute('data-gg-daily-active', 'true');
      section.removeAttribute('data-gg-daily-pending');
      current = key + '|' + product.id + '|' + variantNumber(product.variantId);
      currentProduct = product;
    }

    async function refresh(forceCheck) {
      if (disposed || running) return;
      running = true;
      var request = ++token;
      try {
        var value = dataNode ? dataNode.textContent : '';
        var changed = value !== raw;
        if (changed) {
          raw = value;
          config = JSON.parse(value);
          products = (config.products || []).filter(function (product) { return /^kate\s+aurora\b/i.test(product.title || '') && product.image && product.image.src && Number(product.image.width) > 0 && Number(product.image.height) > 0 && product.variantId && product.url; });
          select = createSelector(products);
        }
        if (!config || config.enabled !== true || !nodes.image || !nodes['image-link']) { fallback(); return; }
        var key = dateKey(Date.now(), config.timezone || TIMEZONE);
        var product = select(key);
        var verify = Boolean(forceCheck || (current && current.slice(0, 10) !== key) || Date.now() - lastChecked >= 300000);
        if (currentProduct && !inWindow(seasonOf(currentProduct), key)) fallback();
        if (verify) {
          var remaining = products.slice();
          product = createSelector(remaining)(key);
          while (product && !(await variantAvailable(product))) {
            remaining = remaining.filter(function (entry) { return String(entry.id) !== String(product.id); });
            product = createSelector(remaining)(key);
          }
          select = createSelector(remaining);
          lastChecked = Date.now();
        }
        if (!product) { fallback(); return; }
        var selectionKey = key + '|' + product.id + '|' + variantNumber(product.variantId);
        if (!changed && selectionKey === current) { section.removeAttribute('data-gg-daily-pending'); return; }
        productURL(product);
        await preload(product, nodes.image.getAttribute('sizes'));
        if (disposed || request !== token) return;
        if (dateKey(Date.now(), config.timezone || TIMEZONE) !== key) { window.setTimeout(function () { refresh(true); }, 0); return; }
        apply(product, key);
      } catch (error) {
        if (!disposed && request === token) fallback();
      } finally {
        running = false;
        schedule();
      }
    }

    return { refresh: refresh, dispose: function () { disposed = true; token += 1; window.clearTimeout(timer); } };
  }

  function scan() {
    controllers.forEach(function (controller, section) { if (!section.isConnected) { controller.dispose(); controllers.delete(section); } });
    document.querySelectorAll('[data-gg-daily-hero]').forEach(function (section) {
      if (!controllers.has(section)) controllers.set(section, makeController(section));
      controllers.get(section).refresh(false);
    });
  }

  function refreshAll() {
    if (document.visibilityState === 'hidden') return;
    controllers.forEach(function (controller) { controller.refresh(false); });
  }

  window.GGDailyHero = { scan: scan };
  window.addEventListener('focus', refreshAll);
  window.addEventListener('pageshow', refreshAll);
  document.addEventListener('visibilitychange', refreshAll);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', function (event) {
    controllers.forEach(function (controller, section) { if (event.target.contains(section)) { controller.dispose(); controllers.delete(section); } });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
})();
